<template>
  <ts-loading-banner :loading="loading">
    <div class="ts-space-y-3">
      <!-- Mission Select -->
      <div class="row">
        <div class="col-md-12">
          <label class="required ts-text-xs">{{
            $t('employeeMission.pageTitle')
          }}</label>
          <a-select
            show-search
            style="width: 100%"
            :filter-option="false"
            v-model="model.mission_id"
            @change="onMissionSelect"
          >
            <a-select-option
              v-for="m in unclaimedMissions"
              :key="m.id"
              :value="m.id"
            >
              {{ m.employee_name }} — {{ m.mission_title }}
            </a-select-option>
          </a-select>
        </div>
      </div>

      <!-- Read-only display fields -->
      <div class="row" v-if="model.mission_id">
        <div class="col-md-6">
          <label class="ts-text-xs text-muted">{{
            $t('allowanceClaimMission.totalAllowance')
          }}</label>
          <input
            :value="model.total_allowance"
            type="text"
            class="form-control"
            readonly
          />
        </div>
        <div class="col-md-6">
          <label class="ts-text-xs text-muted">{{
            $t('allowanceClaimMission.advanceAmount')
          }}</label>
          <input
            :value="model.advance_amount"
            type="text"
            class="form-control"
            readonly
          />
        </div>
      </div>

      <div class="row" v-if="model.mission_id">
        <!-- Deduction Amount -->
        <div class="col-md-6">
          <label class="ts-text-xs">{{
            $t('allowanceClaimMission.deductionAmount')
          }}</label>
          <input
            v-model.number="model.amount_deduction"
            type="number"
            class="form-control"
            @input="recalculate"
          />
        </div>
        <!-- Amount to be Claimed (auto but overridable) -->
        <div class="col-md-6">
          <label class="ts-text-xs">{{
            $t('allowanceClaimMission.amountToBeClaimed')
          }}</label>
          <input
            v-model.number="model.amount_claimed"
            type="number"
            class="form-control"
          />
        </div>
      </div>

      <div class="row" v-if="model.mission_id">
        <div class="col-md-6">
          <label class="ts-text-xs text-muted">{{
            $t('allowanceClaimMission.paymentDue')
          }}</label>
          <input
            :value="capitalize(model.payment_due)"
            type="text"
            class="form-control"
            readonly
          />
        </div>
      </div>

      <div class="row" v-if="model.mission_id">
        <div class="col-md-12">
          <label class="ts-text-xs">{{
            $t('allowanceClaimMission.remark')
          }}</label>
          <textarea
            v-model="model.claim_remark"
            class="form-control"
            rows="3"
          ></textarea>
        </div>
      </div>

      <!-- Slip Upload -->
      <div class="row" v-if="model.mission_id">
        <div class="col-md-12">
          <label class="ts-text-xs">{{
            $t('allowanceClaimMission.attachSlip')
          }}</label>
          <div class="d-flex flex-column align-items-start">
            <!-- Preview -->
            <div v-if="slipPreview" class="mb-2">
              <img
                :src="slipPreview"
                style="max-height: 120px; max-width: 200px; object-fit: contain; border: 1px solid #ddd; border-radius: 4px;"
              />
            </div>
            <!-- Upload input -->
            <input
              type="file"
              ref="slipInput"
              accept="image/*"
              style="display:none"
              @change="onSlipChange"
            />
            <div class="d-flex ts-space-x-2">
              <ts-button @click.prevent="$refs.slipInput.click()">
                <i class="fas fa-upload mr-1"></i>
                {{
                  slipPreview
                    ? $t('allowanceClaimMission.changeFile')
                    : $t('allowanceClaimMission.uploadFile')
                }}
              </ts-button>
              <ts-button
                v-if="slipPreview"
                color="danger"
                @click.prevent="removeSlip"
              >
                <i class="fas fa-times mr-1"></i>
                {{ $t('allowanceClaimMission.remove') }}
              </ts-button>
            </div>
          </div>
        </div>
      </div>
      <div class="row ts-mt-3">
        <div class="col-md-12 text-right ts-space-x-2">
          <ts-button @click.prevent="$emit('cancel')">{{
            $t('cancel')
          }}</ts-button>
          <ts-button
            color="primary"
            :waiting="waiting"
            :disabled="!model.mission_id || waiting"
            @click.prevent="onClaim"
          >
            {{ $t('allowanceClaimMission.claim') }}
          </ts-button>
        </div>
      </div>
    </div>
  </ts-loading-banner>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'MissionClaimForm',
  data() {
    return {
      loading: false,
      waiting: false,
      slipFile: null,
      slipPreview: null,
      model: {
        mission_id: null,
        total_allowance: 0,
        advance_amount: 0,
        amount_deduction: 0,
        amount_claimed: 0,
        payment_due: null,
        claim_remark: null
      }
    }
  },
  computed: {
    ...mapState('mission/missionClaim', ['unclaimedMissions'])
  },
  async mounted() {
    this.loading = true
    await this.$store.dispatch('mission/missionClaim/getUnclaimedMissions')
    this.loading = false
  },
  watch: {
    // Auto-recalculate payment_due when amount_claimed changes
    'model.amount_claimed'(val) {
      this.model.payment_due = val > 0 ? 'employee' : 'company'
    }
  },
  methods: {
    async onMissionSelect(missionId) {
      if (!missionId) return
      this.loading = true
      try {
        const detail = await this.$store.dispatch(
          'mission/missionClaim/getMissionDetail',
          missionId
        )
        this.model.total_allowance = detail.total_allowance || 0
        this.model.advance_amount = detail.advance_amount || 0
        this.model.amount_deduction = detail.amount_deduction || 0
        this.model.amount_claimed = detail.amount_claimed || this.autoCalc()
        this.model.payment_due = detail.payment_due || null
        this.model.claim_remark = detail.claim_remark || null
        this.recalculate()
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    recalculate() {
      this.model.amount_claimed =
        this.model.total_allowance -
        this.model.advance_amount -
        this.model.amount_deduction
      this.model.payment_due =
        this.model.amount_claimed > 0 ? 'employee' : 'company'
    },
    autoCalc() {
      return (
        this.model.total_allowance -
        this.model.advance_amount -
        this.model.amount_deduction
      )
    },
    async onClaim() {
      this.waiting = true
      try {
        const formData = new FormData()
        formData.append('amount_deduction', this.model.amount_deduction)
        formData.append('amount_claimed', this.model.amount_claimed)
        formData.append('payment_due', this.model.payment_due)
        formData.append('claim_remark', this.model.claim_remark || '')
        if (this.slipFile) {
          formData.append('attach_slip', this.slipFile)
        }

        await this.$store.dispatch('mission/missionClaim/claim', {
          missionId: this.model.mission_id,
          data: formData
        })

        this.$toasted.success('Claimed successfully')
        this.$emit('fetchData')
        this.$emit('cancel')
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.waiting = false
      }
    },
    onSlipChange(event) {
      const file = event.target.files[0]
      if (!file) return
      this.slipFile = file
      this.slipPreview = URL.createObjectURL(file)
    },

    removeSlip() {
      this.slipFile = null
      this.slipPreview = null
      this.$refs.slipInput.value = ''
    },

    capitalize(val) {
      if (!val) return ''
      return val.charAt(0).toUpperCase() + val.slice(1)
    },
  }
}
</script>
